import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Alert, Spinner, ListGroup
} from 'react-bootstrap';
import {
  FaUser, FaLock, FaBell, FaTrashAlt
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Layout from '../components/layout/Layout';
import '../components/layout/Settings.css';

const Settings = () => {
  const { currentUser, updateProfile, updatePassword, deleteAccount } = useAuth();
  const { settings, updateSetting } = useSettings();
  const [activeSection, setActiveSection] = useState('account');

  // State for forms
  const [accountInfo, setAccountInfo] = useState({
    displayName: '',
    email: '',
    phone: '',
  });
  const [passwordInfo, setPasswordInfo] = useState({ currentPass: '', newPass: '', confirmPass: '' });
  const [notifications, setNotifications] = useState({ messages: true, orders: true, promotions: false });
  // Loading, error, and success states
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [success, setSuccess] = useState({});

  useEffect(() => {
    if (currentUser) {
      setAccountInfo({
        displayName: currentUser.displayName || '',
        email: currentUser.email || '',
        phone: currentUser.phoneNumber || '',
      });
    }
    


  }, [currentUser, settings]);

  const handleAccountChange = (e) => {
    setAccountInfo({ ...accountInfo, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordInfo({ ...passwordInfo, [e.target.name]: e.target.value });
  };

  const handleSave = async (section) => {
    setLoading({ [section]: true });
    setError({ [section]: '' });
    setSuccess({ [section]: '' });
    
    try {
      if (section === 'account') {
        // await updateProfile({ displayName: accountInfo.displayName, phoneNumber: accountInfo.phone });
        await new Promise(r => setTimeout(r, 800)); // Mock API call
        console.log('Updating profile:', accountInfo);
      } else if (section === 'security') {
        if (passwordInfo.newPass !== passwordInfo.confirmPass) throw new Error("Passwords do not match.");
        // await updatePassword(passwordInfo.currentPass, passwordInfo.newPass);
        await new Promise(r => setTimeout(r, 800)); // Mock API call
        console.log('Updating password');
      }
      setSuccess({ [section]: 'Settings saved successfully!' });
    } catch (err) {
      setError({ [section]: err.message || `Failed to update ${section}.` });
    } finally {
      setLoading({ [section]: false });
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action is irreversible.')) return;
    setLoading({ delete: true });
    try {
      // await deleteAccount();
      await new Promise(r => setTimeout(r, 1000)); // Mock API call
      console.log('Account deleted');
      // Handle post-deletion logic (e.g., redirect)
    } catch (err) {
      setError({ delete: 'Failed to delete account.' });
    } finally {
      setLoading({ delete: false });
    }
  };



  const renderSectionContent = () => {
    switch (activeSection) {
      case 'account':
        return (
          <Card>
            <Card.Header>Account Information</Card.Header>
            <Card.Body>
              <Form onSubmit={(e) => { e.preventDefault(); handleSave('account'); }}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Name</Form.Label>
                  <Form.Control type="text" name="displayName" value={accountInfo.displayName} onChange={handleAccountChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control type="email" name="email" value={accountInfo.email} disabled />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control type="tel" name="phone" value={accountInfo.phone} onChange={handleAccountChange} />
                </Form.Group>
                <Button type="submit" disabled={loading.account}>
                  {loading.account ? <><Spinner as="span" animation="border" size="sm" /> Saving...</> : 'Save Changes'}
                </Button>
                {success.account && <Alert variant="success" className="mt-3">{success.account}</Alert>}
                {error.account && <Alert variant="danger" className="mt-3">{error.account}</Alert>}
              </Form>
            </Card.Body>
          </Card>
        );
      case 'security':
        return (
          <>
            <Card className="mb-4">
              <Card.Header>Change Password</Card.Header>
              <Card.Body>
                <Form onSubmit={(e) => { e.preventDefault(); handleSave('security'); }}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control type="password" name="currentPass" value={passwordInfo.currentPass} onChange={handlePasswordChange} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control type="password" name="newPass" value={passwordInfo.newPass} onChange={handlePasswordChange} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control type="password" name="confirmPass" value={passwordInfo.confirmPass} onChange={handlePasswordChange} />
                  </Form.Group>
                  <Button type="submit" disabled={loading.security}>
                    {loading.security ? <><Spinner as="span" animation="border" size="sm" /> Updating...</> : 'Update Password'}
                  </Button>
                  {success.security && <Alert variant="success" className="mt-3">{success.security}</Alert>}
                  {error.security && <Alert variant="danger" className="mt-3">{error.security}</Alert>}
                </Form>
              </Card.Body>
            </Card>
            <div className="danger-zone">
              <h5><FaTrashAlt /> Delete Account</h5>
              <p>Once you delete your account, there is no going back. Please be certain.</p>
              <Button variant="danger" onClick={handleDeleteAccount} disabled={loading.delete}>
                {loading.delete ? <><Spinner as="span" animation="border" size="sm" /> Deleting...</> : 'Delete My Account'}
              </Button>
              {error.delete && <Alert variant="danger" className="mt-3">{error.delete}</Alert>}
            </div>
          </>
        );
      case 'notifications':
        return (
          <Card>
            <Card.Header>Notifications</Card.Header>
            <Card.Body>
              <Form>
                <Form.Check type="switch" id="notif-messages" label="New Messages" className="mb-3" checked={notifications.messages} onChange={() => setNotifications(p => ({...p, messages: !p.messages}))} />
                <Form.Check type="switch" id="notif-orders" label="Order Updates" className="mb-3" checked={notifications.orders} onChange={() => setNotifications(p => ({...p, orders: !p.orders}))} />
                <Form.Check type="switch" id="notif-promotions" label="Promotions & Offers" checked={notifications.promotions} onChange={() => setNotifications(p => ({...p, promotions: !p.promotions}))} />
              </Form>
            </Card.Body>
          </Card>
        );

      default: return null;
    }
  };

  const sections = [
    { key: 'account', label: 'Account', icon: <FaUser /> },
    { key: 'security', label: 'Security', icon: <FaLock /> },
    { key: 'notifications', label: 'Notifications', icon: <FaBell /> }
  ];

  return (
    <Layout>
      <div className="settings-container">
        <Container>
          <Row>
            <Col lg={3} md={4}>
              <ListGroup className="settings-nav sticky-top">
                {sections.map(sec => (
                  <ListGroup.Item
                    key={sec.key}
                    action
                    active={activeSection === sec.key}
                    onClick={() => setActiveSection(sec.key)}
                  >
                    {sec.icon} {sec.label}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Col>
            <Col lg={9} md={8} className="settings-content">
              {renderSectionContent()}
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default Settings;
